'use client';

import { Checkbox as CheckboxPrimitive } from '@base-ui/react/checkbox';
import { cn } from 'utils/cn';
import { CheckIcon, MinusIcon } from 'lucide-react';

// `variant` chooses the checked/indeterminate fill: `primary` (default, green — unchanged for
// existing consumers) or `secondary` (orange, used by Roles & Permissions; green too in the new design). An indeterminate checkbox shows a dash instead of a check. The box is always 16px.
//
// The disabled styling keys on `data-disabled`, not the `disabled:` variant: the root renders as a
// `<span role="checkbox">`, which never matches `:disabled` however the prop is passed.
//
// The new design sets the mark's stroke itself. A highlighted list row recolours every descendant, the icon's
// `path` included, and a stroke left on `currentColor` follows that colour whatever the `svg` says.
function Checkbox({
    className,
    variant = 'primary',
    ...props
}: CheckboxPrimitive.Root.Props & { variant?: 'primary' | 'secondary' }) {
    return (
        <CheckboxPrimitive.Root
            data-slot="checkbox"
            className={cn(
                'peer border-grey-300 bg-paper group-has-[:focus-visible]/field-label:not-data-checked:border-input focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 aria-invalid:aria-checked:border-primary dark:bg-input/30 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 data-checked:border-primary data-checked:bg-primary data-checked:text-primary-foreground data-indeterminate:border-primary data-indeterminate:bg-primary group-has-[:focus-visible]/field-label:data-checked:border-primary dark:data-checked:bg-primary relative flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-[4px] border shadow-xs transition-shadow outline-none group-has-disabled/field:opacity-50 group-has-[:focus-visible]/field-label:ring-0 after:absolute after:-inset-x-3 after:-inset-y-2 focus-visible:ring-3 aria-invalid:ring-3 data-disabled:cursor-not-allowed data-disabled:opacity-50',
                variant === 'secondary' &&
                    'data-checked:border-secondary data-checked:bg-secondary data-indeterminate:border-secondary data-indeterminate:bg-secondary new:data-checked:border-primary new:data-checked:bg-primary new:data-indeterminate:border-primary new:data-indeterminate:bg-primary',
                className
            )}
            {...props}
        >
            <CheckboxPrimitive.Indicator
                data-slot="checkbox-indicator"
                className="new:[&>svg]:stroke-primary-foreground grid place-content-center text-current transition-none [&>svg]:size-3.5"
            >
                {props.indeterminate ? (
                    <MinusIcon className="text-primary-foreground! stroke-[1.75px]" />
                ) : (
                    <CheckIcon className="text-primary-foreground!" />
                )}
            </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
    );
}

export { Checkbox };
